import i18n from "i18next";
import en from "../translations/en.json";
import { initReactI18next } from "react-i18next";

// Only English (the default + fallback language) is bundled up-front. Each other
// locale is ~60-80 KB of JSON, so they are code-split and fetched on demand the
// first time the app switches to them, instead of being parsed into the initial
// bundle. The list of codes is still exported for UI that needs to enumerate the
// available languages (e.g. the robot language dropdown).
export const supportedLanguages = [
  "de",
  "en",
  "es",
  "fr",
  "it",
  "cs",
  "ja",
  "he",
] as const;

const lazyResourceLoaders: Record<string, () => Promise<{ default: object }>> =
  {
    de: () => import("../translations/de.json"),
    es: () => import("../translations/es.json"),
    fr: () => import("../translations/fr.json"),
    it: () => import("../translations/it.json"),
    cs: () => import("../translations/cs.json"),
    ja: () => import("../translations/ja.json"),
    he: () => import("../translations/he.json"),
  };

// Fetches and registers a language bundle if it has not been loaded yet.
export const loadLanguage = async (lng: string): Promise<void> => {
  if (
    lng === "en" ||
    i18n.hasResourceBundle(lng, "translation") ||
    !lazyResourceLoaders[lng]
  ) {
    return;
  }

  const bundle = (await lazyResourceLoaders[lng]()).default;
  i18n.addResourceBundle(lng, "translation", bundle, true, true);
};

// Safety net: if anything switches language directly via i18n.changeLanguage,
// make sure the corresponding bundle gets fetched. Combined with
// react.bindI18nStore below, components re-render once it arrives.
i18n.on("languageChanged", (lng) => {
  void loadLanguage(lng);
});

i18n
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    fallbackLng: "en",
    debug: import.meta.env.DEV,
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    resources: {
      en: { translation: en },
    },
    react: {
      // Re-render components when a lazily-loaded bundle is added to the store.
      bindI18nStore: "added",
    },
  });

export default i18n;
